import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Crown, Lock, Ticket } from 'lucide-react';
import { formatMoney, friendlyError, getCheckoutConfig, startCheckoutForEvent } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Devuelve el texto legal a mostrar bajo el CTA del paywall según el
 * provider ONE_TIME activo. No expone `mock_test` al usuario final.
 */
function providerNotice(provider) {
  if (provider === 'redsys') return 'Pago seguro gestionado por Redsys.';
  if (provider === 'mock_test') return 'Modo de prueba de pagos activo.';
  return null;
}

/**
 * Paywall reutilizable para eventos premium / pay-per-event.
 *
 * Props:
 *   event: { id, title, slug, access_type, price_cents, currency, club_id }
 *   requires: "purchase" | "subscription" | "subscription_or_purchase" | "unknown"
 */
export function EventPaywall({ event, requires = 'purchase' }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [provider, setProvider] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await getCheckoutConfig();
        if (alive) setProvider(cfg?.default_one_time_provider || null);
      } catch { /* silencioso: si falla, ocultamos el aviso */ }
    })();
    return () => { alive = false; };
  }, []);

  const isPpv = (event?.access_type || '').toLowerCase().includes('pay');
  const showsBuy = isPpv && Number(event?.price_cents) > 0;
  const showsSubscribe = requires !== 'purchase';

  const handleBuy = async () => {
    if (!user) {
      toast.info('Inicia sesión para comprar.');
      navigate(`/acceder?next=/eventos/${event.slug}`);
      return;
    }
    setBusy(true);
    try {
      const session = await startCheckoutForEvent(event.id);
      navigate(`/checkout/sesion/${session.id}`);
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSubscribe = () => navigate('/planes');

  return (
    <Card
      className="relative overflow-hidden rounded-3xl border border-[var(--ns-gold)]/40 bg-gradient-to-br from-[var(--ns-bg-2)] to-white p-6 sm:p-8"
      data-testid="event-paywall"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-11 w-11 rounded-2xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--ns-gold-700)] font-semibold mb-1">
            Contenido premium
          </p>
          <h3 className="text-xl sm:text-2xl font-bold text-[var(--ns-text)]">
            Desbloquea este contenido o suscríbete para verlo
          </h3>
          <p className="text-sm text-[var(--ns-text-muted)] mt-1.5 leading-relaxed">
            Accede al contenido tras el pago. Procesamos pagos de forma segura.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        {showsBuy ? (
          <Button
            type="button"
            onClick={handleBuy}
            disabled={busy}
            className="h-12 rounded-xl bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white font-semibold flex items-center justify-center gap-2"
            data-testid="event-paywall-buy"
          >
            <Ticket className="h-4 w-4" />
            {busy ? 'Procesando…' : `Comprar este contenido — ${formatMoney(event.price_cents, event.currency || 'EUR')}`}
          </Button>
        ) : null}
        {showsSubscribe ? (
          <Button
            type="button"
            variant="outline"
            onClick={handleSubscribe}
            className="h-12 rounded-xl border-[var(--ns-gold)]/60 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/10 font-semibold flex items-center justify-center gap-2"
            data-testid="event-paywall-subscribe"
          >
            <Crown className="h-4 w-4" />
            Ver planes de suscripción
          </Button>
        ) : null}
      </div>
      <p
        className="text-xs text-[var(--ns-text-subtle)] mt-4"
        data-testid="event-paywall-provider-notice"
      >
        {providerNotice(provider)}
      </p>
    </Card>
  );
}

export default EventPaywall;
